import React from "react";
import { BasicStockChart } from "@/components/Charts";
import { BacktestType } from "@/features/backtest";
import { omit, groupBy } from "lodash";
import Highcharts, { Options } from "highcharts/highstock";
import { oc } from "ts-optchain";
import moment from "moment";

type Props = {
  data: BacktestType.ohlcData[];
  indicators: BacktestType.IndicatorData[];
  transactions: BacktestType.TransactionsRecords[];
  ticker: string;
};

export default ({ data, indicators, ticker, transactions }: Props) => {
  const transactionGroup = Object.entries(
    groupBy(
      transactions.map(e => ({ ...e, action: e.value < 0 ? "B" : "S" })),
      "action"
    )
  );

  return (
    <div>
      <BasicStockChart height={700} options={createOption()} />
    </div>
  );

  function createOption(): Options {
    return {
      rangeSelector: {
        selected: 3
      },

      title: {
        text: `${ticker} with indicators`
      },
      legend: {
        enabled: true
      },
      yAxis: [
        {
          labels: {
            align: "right",
            x: -3
          },
          title: {
            text: "OHLC"
          },
          height: "70%",
          lineWidth: 2,
          resize: {
            enabled: true
          }
        },
        {
          labels: {
            align: "right",
            x: -3
          },
          title: {
            text: "Volume"
          },
          top: "75%",
          height: "25%",
          offset: 0,
          lineWidth: 2
        }
      ],

      tooltip: {
        split: true
      },

      series: [
        {
          type: "candlestick",
          name: ticker,
          data: data.map(e => ({ ...omit(e, "volume"), x: e.datetime })),
          color: "#dd5f56",
          upColor: "#51a399",
          id: "ohlc"
        },
        {
          type: "column",
          name: "Volume",
          showInLegend: false,
          data: data.map((e, i) => ({
            y: e.volume,
            x: e.datetime,
            color: e.close - oc(data)[i - 1].close(0) < 0 ? "#dd5f56" : "#51a399",
            opacity: 0.7
          })),
          yAxis: 1
        },
        ...createIndicatorSeries(),
        ...createTransactionFlags()
      ]
    };
  }

  function createIndicatorSeries() {
    return indicators.map(e => ({
      type: "line",
      name: e.indicator
        .toUpperCase()
        .replace(ticker, "")
        .replace("_(", "")
        .replace(")", ""),
      data: e.data.map(d => ({ x: d.datetime, y: d.value }))
    })) as Highcharts.SeriesLineOptions[];
  }

  function createTransactionFlags() {
    return transactionGroup.map(([k, v]) => ({
      y: k === "B" ? -30 : 30,
      type: "flags",
      showInLegend: false,
      onSeries: "ohlc",
      shape: "circlepin",
      width: 16,
      color: k === "B" ? "#4895ec" : "#ec5e59",
      fillColor: k === "B" ? "#4895ec" : "#ec5e59",
      style: {
        // text style
        color: "white"
      },
      data: v.map(e => ({
        x: parseInt(moment(e.date, "YYYY-MM-DD").format("x")),
        title: k,
        text: `${k === "B" ? "Buy" : "Sell"} executed at ${e.price} with size ${e.amount}`
      }))
    })) as Highcharts.SeriesFlagsOptions[];
  }
};
